import React, { useState, useCallback, useEffect } from 'react';
import { X, Check, ChevronRight, ChevronLeft, Calendar } from 'lucide-react';
import AppointmentModal from '@/components/calendar/AppointmentModal';
import { STORAGE_KEYS } from '@/utils/calendarConstants';
import { userStorage } from '../utils/userStorage';
const MONTH_NAMES = ['Januar','Februar','März','April','Mai','Juni','Juli','August','September','Oktober','November','Dezember'];
const WEEKDAY_SHORT = ['Mo','Di','Mi','Do','Fr','Sa','So'];

function getWeekNumber(d) {
  const date = new Date(d);
  date.setHours(0, 0, 0, 0);
  date.setDate(date.getDate() + 3 - ((date.getDay() + 6) % 7));
  const week1 = new Date(date.getFullYear(), 0, 4);
  return 1 + Math.round(((date - week1) / 86400000 - 3 + ((week1.getDay() + 6) % 7)) / 7);
}

function getLocalDateStr(d = new Date()) {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

function loadLS(key, fallback) {
  try { return JSON.parse(userStorage.getItem(key)) ?? fallback; } catch { return fallback; }
}

function getRemainingWeekDays() {
  const days = [];
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  // Zeigt alle verbleibenden Tage der aktuellen Woche (bis einschließlich Sonntag).
  // Weiter zu gehen macht keinen Sinn, weil die "Diese Woche"-Todos-Ansicht
  // bei Sonntag endet.
  for (let i = 0; i < 7; i++) {
    const d = new Date(today);
    d.setDate(today.getDate() + i);
    const dayOfWeek = (d.getDay() + 6) % 7; // 0=Mo, 6=So
    days.push({
      label: i === 0 ? 'Heute' : WEEKDAY_SHORT[dayOfWeek],
      date: getLocalDateStr(d),
      sub: d.toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit' }),
    });
    if (dayOfWeek === 6) break; // nach Sonntag stoppen
  }
  return days;
}

function getRemainingWeeks() {
  const today = new Date();
  const year = today.getFullYear();
  const month = today.getMonth();
  const currentWeek = getWeekNumber(today);
  const weeks = [];
  const lastDay = new Date(year, month + 1, 0);
  for (let w = currentWeek; w <= getWeekNumber(lastDay); w++) {
    weeks.push({ key: `${year}-W${w}`, label: `KW ${w}` });
  }
  return weeks;
}

function getRemainingMonths() {
  const today = new Date();
  const year = today.getFullYear();
  const months = [];
  for (let m = today.getMonth(); m < 12; m++) {
    months.push({
      key: `${year}-${String(m + 1).padStart(2, '0')}`,
      label: MONTH_NAMES[m],
    });
  }
  return months;
}

const MAIN_CATEGORIES = [
  { id: 'todos',    label: "Bei To-Do's", icon: '✅', color: 'bg-emerald-50 text-emerald-700 hover:bg-emerald-100', desc: 'Auch als Todo hinzufügen' },
  { id: 'contact',  label: 'Im Kontakt',  icon: '📋', color: 'bg-slate-100 text-slate-700 hover:bg-slate-200',    desc: 'Nur für diesen Kontakt speichern' },
  { id: 'calendar', label: 'In Kalender', icon: '📆', color: 'bg-blue-50 text-blue-700 hover:bg-blue-100',         desc: 'Als Kalendertermin anlegen' },
];

const TODO_DESTINATIONS = [
  { id: 'daily',   label: 'Heute',       icon: '☀️',  color: 'bg-emerald-50 text-emerald-700 hover:bg-emerald-100' },
  { id: 'weekly',  label: 'Woche',       icon: '📅',  color: 'bg-blue-50 text-blue-700 hover:bg-blue-100', hasSubStep: true },
  { id: 'monthly', label: 'Monat',       icon: '🗓️', color: 'bg-purple-50 text-purple-700 hover:bg-purple-100', hasSubStep: true },
  { id: 'open',    label: 'Dieses Jahr', icon: '🎯',  color: 'bg-orange-50 text-orange-700 hover:bg-orange-100', hasSubStep: true },
];

const CALENDAR_VIEWS = [
  { id: 'week',  label: 'Wochenansicht', icon: '📅', color: 'bg-blue-50 text-blue-700 hover:bg-blue-100',       desc: 'Termin in der Wochenansicht' },
  { id: 'month', label: 'Monatsansicht', icon: '🗓️', color: 'bg-purple-50 text-purple-700 hover:bg-purple-100', desc: 'Termin in der Monatsansicht' },
  { id: 'year',  label: 'Jahresansicht', icon: '🎯',  color: 'bg-orange-50 text-orange-700 hover:bg-orange-100', desc: 'Termin in der Jahresplanung' },
];

function getPriorityColor(p) {
  if (p >= 9) return '#ef4444';
  if (p >= 7) return '#f97316';
  if (p >= 5) return '#eab308';
  if (p >= 3) return '#22c55e';
  return '#94a3b8';
}

// Simple inline form for Month / Year calendar entries
function CalendarEntryForm({ title, onTitleChange, note, onNoteChange, from, onFromChange, to, onToChange, priority, onPriorityChange, onSave, onCancel, contactName, isYear }) {
  const todayStr = getLocalDateStr();
  return (
    <div className="space-y-4">
      {/* Contact badge */}
      {contactName && (
        <div className="flex items-center gap-2 text-xs">
          <span className="px-2 py-1 bg-blue-50 text-blue-700 rounded-lg font-medium flex items-center gap-1">
            <Calendar className="w-3 h-3" />
            Kontakt: {contactName}
          </span>
        </div>
      )}

      {/* Title */}
      <div>
        <label className="block text-xs font-medium text-slate-700 mb-1">Titel *</label>
        <input
          type="text"
          autoFocus
          value={title}
          onChange={e => onTitleChange(e.target.value)}
          placeholder="Titel des Termins"
          className="w-full p-3 border border-slate-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-blue-400"
        />
      </div>

      {/* Note */}
      <div>
        <label className="block text-xs font-medium text-slate-700 mb-1">Notiz (optional)</label>
        <textarea
          value={note}
          onChange={e => onNoteChange(e.target.value)}
          placeholder="Zusätzliche Informationen..."
          rows={2}
          className="w-full p-3 border border-slate-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-blue-400 resize-none"
        />
      </div>

      {/* Date(s) */}
      <div>
        <label className="block text-xs font-medium text-slate-700 mb-1">Datum</label>
        <input
          type="date"
          value={from}
          min={todayStr}
          onChange={e => onFromChange(e.target.value)}
          className="w-full p-2.5 border border-slate-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-blue-400"
        />
      </div>

      {/* Priority */}
      <div>
        <div className="flex items-center justify-between mb-1">
          <label className="text-xs font-medium text-slate-700">Priorität</label>
          <span className="text-sm font-bold" style={{ color: getPriorityColor(priority) }}>{priority}</span>
        </div>
        <input
          type="range" min="1" max="10" value={priority}
          onChange={e => onPriorityChange(Number(e.target.value))}
          className="w-full h-2 rounded-full appearance-none cursor-pointer"
          style={{ background: `linear-gradient(to right, ${getPriorityColor(priority)} 0%, ${getPriorityColor(priority)} ${(priority - 1) / 9 * 100}%, #e2e8f0 ${(priority - 1) / 9 * 100}%, #e2e8f0 100%)` }}
        />
        <div className="flex justify-between text-xs text-slate-400 mt-0.5">
          <span>Niedrig</span><span>Hoch</span>
        </div>
      </div>

      {/* Buttons */}
      <div className="flex gap-2 pt-1">
        <button type="button" onClick={onCancel}
          className="flex-1 py-2.5 border border-slate-200 text-slate-600 hover:bg-slate-50 rounded-xl text-sm font-medium transition-all">
          Abbrechen
        </button>
        <button type="button" onClick={onSave} disabled={!title.trim()}
          className="flex-1 py-2.5 bg-blue-500 hover:bg-blue-600 disabled:opacity-40 text-white rounded-xl text-sm font-semibold flex items-center justify-center gap-1.5 transition-all">
          <Check className="w-4 h-4" />
          Speichern
        </button>
      </div>
    </div>
  );
}

export default function ZuErledigienModal({ isOpen, onClose, onSave, contactName, contactId, contactTyp }) {
  const [step, setStep] = useState('main');
  const [mainCategory, setMainCategory] = useState(null);
  const [destination, setDestination] = useState(null);
  const [text, setText] = useState('');
  const [note, setNote] = useState('');
  const [dueDate, setDueDate] = useState('');
  const [priority, setPriority] = useState(5);
  const [specificKey, setSpecificKey] = useState(null);

  // Calendar inline form states (for Month and Year)
  const [showAppointment, setShowAppointment] = useState(false); // Week: AppointmentModal
  const [showMonthForm, setShowMonthForm] = useState(false);     // Month: inline form
  const [showYearForm, setShowYearForm] = useState(false);       // Year: inline form
  const [calTitle, setCalTitle] = useState('');
  const [calNote, setCalNote] = useState('');
  const [calFrom, setCalFrom] = useState('');
  const [calTo, setCalTo] = useState('');
  const [calPriority, setCalPriority] = useState(5);

  // Freeze contactId/contactTyp when modal opens so they don't change mid-flow
  const [frozenContactId, setFrozenContactId] = useState(null);
  const [frozenContactTyp, setFrozenContactTyp] = useState(null);

  useEffect(() => {
    if (isOpen) {
      setFrozenContactId(contactId);
      setFrozenContactTyp(contactTyp);
      setCalFrom(getLocalDateStr());
      setCalTo(getLocalDateStr());
      // Body-Scroll sperren
      document.body.style.overflow = 'hidden';
      document.body.style.position = 'fixed';
      document.body.style.width = '100%';
      document.body.style.top = `-${window.scrollY}px`;
    } else {
      // Body-Scroll wiederherstellen
      const scrollY = document.body.style.top;
      document.body.style.overflow = '';
      document.body.style.position = '';
      document.body.style.width = '';
      document.body.style.top = '';
      if (scrollY) window.scrollTo(0, -parseInt(scrollY || '0'));
    }
    return () => {
      document.body.style.overflow = '';
      document.body.style.position = '';
      document.body.style.width = '';
      document.body.style.top = '';
    };
  }, [isOpen, contactId, contactTyp]);

  const weekDays = getRemainingWeekDays();
  const remainingWeeks = getRemainingWeeks();
  const remainingMonths = getRemainingMonths();

  const reset = useCallback(() => {
    setStep('main'); setMainCategory(null); setDestination(null);
    setText(''); setNote(''); setDueDate(''); setPriority(5); setSpecificKey(null);
    setShowAppointment(false); setShowMonthForm(false); setShowYearForm(false);
    setCalTitle(''); setCalNote(''); setCalFrom(''); setCalTo(''); setCalPriority(5);
  }, []);

  const handleClose = () => { reset(); onClose(); };

  const handleMainSelect = (cat) => {
    setMainCategory(cat);
    if (cat === 'contact') {
      setDestination('contact_only'); setStep('form');
    } else if (cat === 'todos') {
      setStep('todo_sub');
    } else {
      // calendar: go directly to view selection (no form step needed)
      setStep('calendar_view');
    }
  };

  const handleTodoSubSelect = (dest) => {
    setDestination(dest);
    if (dest === 'daily') { setSpecificKey(getLocalDateStr()); setStep('form'); }
    else if (dest === 'weekly') { setStep('week_day'); }
    else if (dest === 'monthly') { setStep('month_week'); }
    else if (dest === 'open') { setStep('year_month'); }
  };

  const handleSubKeySelect = (key) => { setSpecificKey(key); setStep('form'); };

  // Calendar view selection → open corresponding modal/form
  const handleCalendarViewSelect = (view) => {
    if (view === 'week') { setShowAppointment(true); }
    else if (view === 'month') { setShowMonthForm(true); }
    else if (view === 'year') { setShowYearForm(true); }
  };

  const handleBack = () => {
    if (step === 'form') {
      if (mainCategory === 'todos') { setStep('todo_sub'); setDestination(null); setSpecificKey(null); }
      else { setStep('main'); setMainCategory(null); }
    } else if (step === 'week_day' || step === 'month_week' || step === 'year_month') {
      setStep('todo_sub'); setDestination(null); setSpecificKey(null);
    } else if (step === 'todo_sub') {
      setStep('main'); setMainCategory(null);
    } else if (step === 'calendar_view') {
      // Close any open sub-forms first, or go back to main
      if (showMonthForm) { setShowMonthForm(false); return; }
      if (showYearForm)  { setShowYearForm(false);  return; }
      setStep('main'); setMainCategory(null);
    }
  };

  // ── Todo + Contact Save ───────────────────────────────────────────────────
  const handleSaveToTodo = () => {
    if (!text.trim()) return;
    const taskId = `task-${Date.now()}`;
    const todoId = Date.now();
    const now = new Date().toISOString();
    const todoBase = {
      id: todoId, text: text.trim(), note: note.trim() || null, priority, done: false,
      dueDate: dueDate || null, createdAt: now,
      contactRef: frozenContactId ? { id: frozenContactId, typ: frozenContactTyp || 'teampartner', name: contactName || null, taskId } : undefined,
    };
    if (destination === 'daily') {
      const key = specificKey || getLocalDateStr();
      const stored = loadLS(STORAGE_KEYS.dailyTodos, {});
      stored[key] = [...(stored[key] || []), todoBase].sort((a, b) => b.priority - a.priority);
      userStorage.setItem(STORAGE_KEYS.dailyTodos, JSON.stringify(stored));
    } else if (destination === 'weekly') {
      // specificKey kann sein:
      //   - 'YYYY-Wxx' (allgemeiner Wochen-Key)
      //   - 'YYYY-MM-DD' (konkreter Tag dieser Woche)
      let weekKey;
      let weekdayIdx = null;
      if (specificKey && /^\d{4}-\d{2}-\d{2}$/.test(specificKey)) {
        const d = new Date(specificKey);
        weekKey = `${d.getFullYear()}-W${getWeekNumber(d)}`;
        weekdayIdx = (d.getDay() + 6) % 7; // 0=Mo, 6=So
      } else {
        weekKey = specificKey || (() => { const n = new Date(); return `${n.getFullYear()}-W${getWeekNumber(n)}`; })();
      }
      const enriched = weekdayIdx !== null ? { ...todoBase, weekday: weekdayIdx } : todoBase;
      const stored = loadLS(STORAGE_KEYS.weeklyTodos, {});
      stored[weekKey] = [...(stored[weekKey] || []), enriched].sort((a, b) => b.priority - a.priority);
      userStorage.setItem(STORAGE_KEYS.weeklyTodos, JSON.stringify(stored));
    } else if (destination === 'monthly') {
      // specificKey kann sein:
      //   - 'YYYY-MM'  (allgemeiner Monats-Key)
      //   - 'YYYY-Wxx' (konkrete KW dieses Monats)
      let monthKey;
      let weekNum = null;
      if (specificKey && /^\d{4}-W\d+$/.test(specificKey)) {
        weekNum = parseInt(specificKey.split('-W')[1], 10);
        const n = new Date();
        monthKey = `${n.getFullYear()}-${String(n.getMonth() + 1).padStart(2, '0')}`;
      } else {
        monthKey = specificKey || (() => { const n = new Date(); return `${n.getFullYear()}-${String(n.getMonth() + 1).padStart(2, '0')}`; })();
      }
      const enriched = weekNum !== null ? { ...todoBase, weekNum } : todoBase;
      const stored = loadLS(STORAGE_KEYS.monthlyTodos, {});
      stored[monthKey] = [...(stored[monthKey] || []), enriched].sort((a, b) => b.priority - a.priority);
      userStorage.setItem(STORAGE_KEYS.monthlyTodos, JSON.stringify(stored));
    } else if (destination === 'open') {
      // „Dieses Jahr"-Backlog:
      //   - ohne specificKey        → reiner Backlog-Eintrag
      //   - mit specificKey YYYY-MM → Backlog-Eintrag mit targetMonth-Label
      const enriched = (specificKey && /^\d{4}-\d{2}$/.test(specificKey))
        ? { ...todoBase, targetMonth: specificKey }
        : todoBase;
      const stored = loadLS(STORAGE_KEYS.backlogTodos, []);
      stored.push(enriched);
      userStorage.setItem(STORAGE_KEYS.backlogTodos, JSON.stringify(stored));
    }
    window.dispatchEvent(new CustomEvent('burningteam-todos-sync'));
    onSave({ id: taskId, text: text.trim(), note: note.trim() || null, dueDate: dueDate || null, done: false, todoRef: todoId, createdAt: now });
    reset(); onClose();
  };

  const handleSaveContactOnly = () => {
    if (!text.trim()) return;
    onSave({ id: `task-${Date.now()}`, text: text.trim(), note: note.trim() || null, dueDate: dueDate || null, done: false, todoRef: null, createdAt: new Date().toISOString() });
    reset(); onClose();
  };

  // ── Calendar Saves ────────────────────────────────────────────────────────

  // Week: AppointmentModal saves (same as before, but contactRef gets taskId)
  const handleSaveWeekAppointment = (apt) => {
    const taskId = `task-${Date.now()}`;
    const contactRefWithTask = frozenContactId
      ? { id: frozenContactId, typ: frozenContactTyp || 'teampartner', name: contactName || null, taskId }
      : (apt.contactRef || null);
    const aptWithContact = { ...apt, contactRef: contactRefWithTask };
    const stored = loadLS(STORAGE_KEYS.appointments, []);
    stored.push(aptWithContact);
    userStorage.setItem(STORAGE_KEYS.appointments, JSON.stringify(stored));

    // Kaskade: auch in Monat und Jahr speichern
    if (apt.date) {
      const weekMonthKey = apt.date.substring(0, 7);
      const cascadeItem = {
        id: aptWithContact.id, title: apt.title, from: apt.date,
        to: apt.endDate || apt.date, note: apt.note || null,
        priority: apt.priority || 5, color: apt.color || '',
        contactRef: contactRefWithTask, syncedFrom: 'week',
      };
      const monthStored = loadLS('zielplanung_month_appointments', {});
      monthStored[weekMonthKey] = [...(monthStored[weekMonthKey] || []), cascadeItem];
      userStorage.setItem('zielplanung_month_appointments', JSON.stringify(monthStored));
      const yearStored = loadLS('zielplanung_year_planning', {});
      yearStored[weekMonthKey] = [...(yearStored[weekMonthKey] || []), cascadeItem];
      userStorage.setItem('zielplanung_year_planning', JSON.stringify(yearStored));
    }

    onSave({ id: taskId, text: apt.title, note: apt.note || null, dueDate: apt.date || null, done: false, todoRef: null, createdAt: new Date().toISOString() });
    setShowAppointment(false);
    reset(); onClose();
  };

  // Month: inline form saves to 'zielplanung_month_appointments' AND 'zielplanung_year_planning'
  const handleSaveMonthAppointment = () => {
    if (!calTitle.trim()) return;
    const taskId = `task-${Date.now()}`;
    const contactRefWithTask = frozenContactId
      ? { id: frozenContactId, typ: frozenContactTyp || 'teampartner', name: contactName || null, taskId }
      : null;
    const dateStr = calFrom || getLocalDateStr();
    const monthKey = dateStr.substring(0, 7);
    const itemId = Date.now();
    const appointment = {
      id: itemId, title: calTitle.trim(), from: dateStr, to: dateStr,
      note: calNote.trim() || null, priority: calPriority, color: '',
      contactRef: contactRefWithTask,
    };
    // Monat speichern
    const monthStored = loadLS('zielplanung_month_appointments', {});
    monthStored[monthKey] = [...(monthStored[monthKey] || []), appointment];
    userStorage.setItem('zielplanung_month_appointments', JSON.stringify(monthStored));
    // Kaskade: auch in Jahresplanung speichern
    const yearStored = loadLS('zielplanung_year_planning', {});
    yearStored[monthKey] = [...(yearStored[monthKey] || []), { ...appointment, syncedFrom: 'month' }];
    userStorage.setItem('zielplanung_year_planning', JSON.stringify(yearStored));
    onSave({ id: taskId, text: calTitle.trim(), note: calNote.trim() || null, dueDate: dateStr, done: false, todoRef: null, createdAt: new Date().toISOString() });
    setShowMonthForm(false);
    reset(); onClose();
  };

  // Year: inline form saves ONLY to 'zielplanung_year_planning' (nicht in Monat)
  const handleSaveYearItem = () => {
    if (!calTitle.trim()) return;
    const taskId = `task-${Date.now()}`;
    const contactRefWithTask = frozenContactId
      ? { id: frozenContactId, typ: frozenContactTyp || 'teampartner', name: contactName || null, taskId }
      : null;
    const fromStr = calFrom || getLocalDateStr();
    const monthKey = fromStr.substring(0, 7);
    const item = {
      id: Date.now(), title: calTitle.trim(), from: fromStr, to: fromStr,
      note: calNote.trim() || null, priority: calPriority, color: '',
      contactRef: contactRefWithTask,
      yearOnly: true, // Nicht in Monatsansicht anzeigen
    };
    const stored = loadLS('zielplanung_year_planning', {});
    stored[monthKey] = [...(stored[monthKey] || []), item];
    userStorage.setItem('zielplanung_year_planning', JSON.stringify(stored));
    onSave({ id: taskId, text: calTitle.trim(), note: calNote.trim() || null, dueDate: fromStr, done: false, todoRef: null, createdAt: new Date().toISOString() });
    setShowYearForm(false);
    reset(); onClose();
  };

  const todayStr = getLocalDateStr();
  if (!isOpen) return null;

  // Week AppointmentModal renders as its own overlay
  const contactRefForWeek = frozenContactId
    ? { id: frozenContactId, typ: frozenContactTyp || 'teampartner', name: contactName || null }
    : null;

  return (
    <>
      {/* Main ZuErledigienModal – hidden when AppointmentModal is open */}
      {!showAppointment && (
        <div className="fixed inset-0 z-[200] flex items-center justify-center">
          <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" onClick={handleClose} />
          <div className="relative bg-white rounded-t-2xl sm:rounded-2xl shadow-2xl w-full max-w-sm mx-0 sm:mx-4 max-h-[90vh] overflow-y-auto">

            {/* Header */}
            <div className="flex items-center justify-between p-4 border-b border-slate-100">
              <div className="flex items-center gap-2">
                {step !== 'main' && (
                  <button type="button" onClick={handleBack} className="p-1 rounded-lg hover:bg-slate-100">
                    <ChevronLeft className="w-4 h-4 text-slate-500" />
                  </button>
                )}
                <div>
                  <h3 className="font-semibold text-slate-800 text-sm">Aufgabe hinzufügen</h3>
                  {contactName && <p className="text-xs text-slate-500">für {contactName}</p>}
                </div>
              </div>
              <button type="button" onClick={handleClose} className="p-1.5 rounded-lg hover:bg-slate-100">
                <X className="w-4 h-4 text-slate-500" />
              </button>
            </div>

            <div className="p-4">

              {/* Step 1: Main category */}
              {step === 'main' && (
                <div className="space-y-2">
                  <p className="text-xs text-slate-500 font-medium mb-3">Wo soll die Aufgabe gespeichert werden?</p>
                  {MAIN_CATEGORIES.map(cat => (
                    <button key={cat.id} type="button" onClick={() => handleMainSelect(cat.id)}
                      className={`w-full py-3 px-4 rounded-xl text-sm font-medium flex items-center justify-between transition-all ${cat.color}`}>
                      <div className="flex flex-col items-start">
                        <span>{cat.icon} {cat.label}</span>
                        <span className="text-xs opacity-60 font-normal">{cat.desc}</span>
                      </div>
                      <ChevronRight className="w-4 h-4 opacity-60 flex-shrink-0" />
                    </button>
                  ))}
                </div>
              )}

              {/* Step 2a: Todo sub-destination */}
              {step === 'todo_sub' && (
                <div className="space-y-2">
                  <p className="text-xs text-slate-500 font-medium mb-3">Zu welchem Todo-Zeitraum?</p>
                  {TODO_DESTINATIONS.map(d => (
                    <button key={d.id} type="button" onClick={() => handleTodoSubSelect(d.id)}
                      className={`w-full py-3 px-4 rounded-xl text-sm font-medium flex items-center justify-between transition-all ${d.color}`}>
                      <span>{d.icon} {d.label}</span>
                      <ChevronRight className="w-4 h-4 opacity-60" />
                    </button>
                  ))}
                </div>
              )}

              {/* Step 2c: Week → specific day */}
              {step === 'week_day' && (
                <div className="space-y-2">
                  <p className="text-xs text-slate-500 font-medium mb-3">Welcher Tag dieser Woche?</p>
                  <button type="button" onClick={() => handleSubKeySelect((() => { const n = new Date(); return `${n.getFullYear()}-W${getWeekNumber(n)}`; })())}
                    className="w-full py-2.5 px-4 rounded-xl text-sm font-medium flex items-center justify-between bg-blue-50 text-blue-700 hover:bg-blue-100 transition-all">
                    <span>📅 Diese Woche (allgemein)</span><ChevronRight className="w-4 h-4 opacity-60" />
                  </button>
                  {weekDays.map(d => (
                    <button key={d.date} type="button" onClick={() => { handleSubKeySelect(d.date); }}
                      className="w-full py-2.5 px-4 rounded-xl text-sm font-medium flex items-center justify-between bg-slate-50 text-slate-700 hover:bg-emerald-50 hover:text-emerald-700 transition-all">
                      <span>{d.label}</span><span className="text-xs opacity-60">{d.sub}</span>
                    </button>
                  ))}
                </div>
              )}

              {/* Step 2d: Month → specific week */}
              {step === 'month_week' && (
                <div className="space-y-2">
                  <p className="text-xs text-slate-500 font-medium mb-3">Welche Woche dieses Monats?</p>
                  <button type="button" onClick={() => { setDestination('monthly'); handleSubKeySelect((() => { const n = new Date(); return `${n.getFullYear()}-${String(n.getMonth()+1).padStart(2,'0')}`; })()); }}
                    className="w-full py-2.5 px-4 rounded-xl text-sm font-medium flex items-center justify-between bg-purple-50 text-purple-700 hover:bg-purple-100 transition-all">
                    <span>🗓️ Dieser Monat (allgemein)</span><ChevronRight className="w-4 h-4 opacity-60" />
                  </button>
                  {remainingWeeks.map(w => (
                    <button key={w.key} type="button" onClick={() => { setDestination('monthly'); handleSubKeySelect(w.key); }}
                      className="w-full py-2.5 px-4 rounded-xl text-sm font-medium flex items-center justify-between bg-slate-50 text-slate-700 hover:bg-purple-50 hover:text-purple-700 transition-all">
                      <span>{w.label}</span><ChevronRight className="w-4 h-4 opacity-60" />
                    </button>
                  ))}
                </div>
              )}

              {/* Step 2e: Year → specific month */}
              {step === 'year_month' && (
                <div className="space-y-2">
                  <p className="text-xs text-slate-500 font-medium mb-3">Welcher Monat dieses Jahr?</p>
                  <button type="button" onClick={() => { setDestination('open'); handleSubKeySelect(null); }}
                    className="w-full py-2.5 px-4 rounded-xl text-sm font-medium flex items-center justify-between bg-orange-50 text-orange-700 hover:bg-orange-100 transition-all">
                    <span>🎯 Dieses Jahr (allgemein)</span><ChevronRight className="w-4 h-4 opacity-60" />
                  </button>
                  {remainingMonths.map(m => (
                    <button key={m.key} type="button" onClick={() => { setDestination('open'); handleSubKeySelect(m.key); }}
                      className="w-full py-2.5 px-4 rounded-xl text-sm font-medium flex items-center justify-between bg-slate-50 text-slate-700 hover:bg-orange-50 hover:text-orange-700 transition-all">
                      <span>{m.label}</span><ChevronRight className="w-4 h-4 opacity-60" />
                    </button>
                  ))}
                </div>
              )}

              {/* Step 2b: Calendar view selector OR inline forms */}
              {step === 'calendar_view' && (
                <div className="space-y-2">
                  {/* Inline Month Form */}
                  {showMonthForm ? (
                    <CalendarEntryForm
                      title={calTitle} onTitleChange={setCalTitle}
                      note={calNote}   onNoteChange={setCalNote}
                      from={calFrom}   onFromChange={setCalFrom}
                      to={calTo}       onToChange={setCalTo}
                      priority={calPriority} onPriorityChange={setCalPriority}
                      onSave={handleSaveMonthAppointment}
                      onCancel={() => setShowMonthForm(false)}
                      contactName={contactName}
                      isYear={false}
                    />
                  ) : showYearForm ? (
                    /* Inline Year Form */
                    <CalendarEntryForm
                      title={calTitle} onTitleChange={setCalTitle}
                      note={calNote}   onNoteChange={setCalNote}
                      from={calFrom}   onFromChange={setCalFrom}
                      to={calTo}       onToChange={setCalTo}
                      priority={calPriority} onPriorityChange={setCalPriority}
                      onSave={handleSaveYearItem}
                      onCancel={() => setShowYearForm(false)}
                      contactName={contactName}
                      isYear={true}
                    />
                  ) : (
                    /* Calendar view buttons */
                    <>
                      <p className="text-xs text-slate-500 font-medium mb-3">Welche Kalenderansicht?</p>
                      {CALENDAR_VIEWS.map(v => (
                        <button key={v.id} type="button" onClick={() => handleCalendarViewSelect(v.id)}
                          className={`w-full py-3 px-4 rounded-xl text-sm font-medium flex items-center justify-between transition-all ${v.color}`}>
                          <div className="flex flex-col items-start">
                            <span>{v.icon} {v.label}</span>
                            <span className="text-xs opacity-60 font-normal">{v.desc}</span>
                          </div>
                          <ChevronRight className="w-4 h-4 opacity-60" />
                        </button>
                      ))}
                    </>
                  )}
                </div>
              )}

              {/* Step 3: Form (for todos & contact_only) */}
              {step === 'form' && (
                <div className="space-y-4">
                  {/* Destination badge */}
                  <div className="flex items-center gap-2 text-xs text-slate-500">
                    <span className="px-2 py-1 bg-slate-100 rounded-lg font-medium">
                      {mainCategory === 'contact' ? '📋 Im Kontakt' : `✅ Bei To-Do's → ${TODO_DESTINATIONS.find(d => d.id === destination)?.label || ''}`}
                    </span>
                  </div>
                  {/* Task text */}
                  <div>
                    <label className="block text-xs font-medium text-slate-700 mb-1">Aufgabe *</label>
                    <input type="text" autoFocus value={text} onChange={e => setText(e.target.value)}
                      onKeyDown={e => { if (e.key === 'Enter') destination === 'contact_only' ? handleSaveContactOnly() : handleSaveToTodo(); }}
                      placeholder="Was muss erledigt werden?"
                      className="w-full p-3 border border-slate-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500" />
                  </div>
                  {/* Notizen */}
                  <div>
                    <label className="block text-xs font-medium text-slate-700 mb-1">Notiz (optional)</label>
                    <textarea value={note} onChange={e => setNote(e.target.value)} placeholder="Zusätzliche Informationen..." rows={2}
                      className="w-full p-3 border border-slate-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500 resize-none" />
                  </div>
                  {/* Due date */}
                  <div>
                    <label className="block text-xs font-medium text-slate-700 mb-1">Fällig bis (optional)</label>
                    <input type="date" value={dueDate} min={todayStr} onChange={e => setDueDate(e.target.value)}
                      className="w-full p-3 border border-slate-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500" />
                  </div>
                  {/* Priority slider (todos only) */}
                  {destination !== 'contact_only' && (
                    <div>
                      <div className="flex items-center justify-between mb-1">
                        <label className="text-xs font-medium text-slate-700">Priorität</label>
                        <span className="text-sm font-bold" style={{ color: getPriorityColor(priority) }}>{priority}</span>
                      </div>
                      <input type="range" min="1" max="10" value={priority} onChange={e => setPriority(Number(e.target.value))}
                        className="w-full h-2 rounded-full appearance-none cursor-pointer"
                        style={{ background: `linear-gradient(to right, ${getPriorityColor(priority)} 0%, ${getPriorityColor(priority)} ${(priority - 1) / 9 * 100}%, #e2e8f0 ${(priority - 1) / 9 * 100}%, #e2e8f0 100%)` }} />
                      <div className="flex justify-between text-xs text-slate-400 mt-0.5">
                        <span>Niedrig</span><span>Hoch</span>
                      </div>
                    </div>
                  )}
                  <button type="button" onClick={destination === 'contact_only' ? handleSaveContactOnly : handleSaveToTodo}
                    disabled={!text.trim()}
                    className="w-full py-3 bg-emerald-500 hover:bg-emerald-600 disabled:opacity-40 text-white rounded-xl text-sm font-semibold flex items-center justify-center gap-2 transition-all">
                    <Check className="w-4 h-4" />
                    Aufgabe speichern
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Week: AppointmentModal renders as its own overlay (cancel → back to calendar_view) */}
      {showAppointment && (
        <AppointmentModal
          isOpen={showAppointment}
          onClose={() => setShowAppointment(false)}
          onSave={handleSaveWeekAppointment}
          onDelete={() => setShowAppointment(false)}
          selectedDate={todayStr}
          prefilledContactRef={contactRefForWeek}
          hideMultiDay={true}
        />
      )}
    </>
  );
}
